import { useTranslations } from 'next-intl';

import { Checkbox } from '@flipster/react/design-system/checkbox';
import { Dialog } from '@flipster/react/design-system/dialog';
import { dialog } from '@flipster/react/design-system/dialog/layout';
import { css } from '@flipster/styled-system/css';
import { stack } from '@flipster/styled-system/patterns';
import type { Deferred } from '@flipster/utils/deferred';

import {
  setOffHoursAlertDontShowAgain,
  shouldShowOffHoursAlert,
  type ShouldShowOffHoursAlertParams,
} from './off-hours';

/**
 * ERD-240 §8.2 다이얼로그 변형:
 * - order (변형 A): Long/Short 주문 제출 — Cancel order(취소) / Proceed(제출 진행)
 * - notice (변형 B): 포지션/주문 관리 액션 — Got It(확인 후 진행)
 */
export type OffHoursAlertVariant = 'order' | 'notice';

interface OffHoursAlertDialogProps {
  isOpen: boolean;
  onExit: () => void;
  variant: OffHoursAlertVariant;
  /** dialog.open 이 주입하는 deferred. Got It/Proceed → resolve(진행), Cancel order/닫힘 → reject(취소). */
  promise: Deferred<void>;
}

export const OffHoursAlertDialog = ({ isOpen, onExit, variant, promise }: OffHoursAlertDialogProps) => {
  const t = useTranslations();
  let isChecked = false;
  const confirm = () => {
    if (isChecked) setOffHoursAlertDontShowAgain(true);
    promise.resolve();
  };
  return (
    <Dialog.Root isOpen={isOpen} onExit={onExit}>
      <Dialog.Title label={t('Trade.r260702.Market_off_hour_rules')} />
      <Dialog.Content>
        <div className={stack({ gap: 24 })}>
          <div className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
            {t('Trade.r260702.Market_off_hour_rules_body')}
          </div>
          <Checkbox onChange={(e) => (isChecked = e.target.checked)} css={{ color: 'content.default.level-3' }}>
            {t('Autogen_common.r251126.s_Dont_show_again')}
          </Checkbox>
        </div>
      </Dialog.Content>
      <Dialog.Footer>
        {variant === 'order' ? (
          <>
            <Dialog.Button
              hierarchy='secondary'
              color='neutral'
              label={t('Assets.r250401.Cancel_order')}
              onClick={() => promise.reject(new Error('Off-hours alert: order cancelled'))}
            />
            <Dialog.Button
              hierarchy='primary'
              color='accent'
              label={t('Autogen_common.r251126.s_Proceed')}
              onClick={confirm}
            />
          </>
        ) : (
          <Dialog.Button
            hierarchy='primary'
            color='accent'
            label={t('Autogen_common.r251126.s_Got_it')}
            onClick={confirm}
          />
        )}
      </Dialog.Footer>
    </Dialog.Root>
  );
};

// DialogLayout(디자인 시스템)은 다른 다이얼로그가 pending이면 open 요청을 조용히 드랍하고
// promise를 settle하지 않는다(dialog/layout.tsx). 디자인 시스템은 수정 금지(AGENTS.md)라 이쪽에서 방어:
// ① 자체 가드 다이얼로그 중복 오픈 방지(in-flight), ② 드랍 감지 — grace 내에 마운트되지 않으면
// 열리지 않은 것으로 보고 취소(false) 처리해 guarded 액션이 영구 hang하는 것을 막는다.
let isGuardDialogInFlight = false;
const dialogOpenGraceMs = 1000;

/**
 * 판정(shouldShowOffHoursAlert)을 재사용하면서, 필요할 때만 off-hours 다이얼로그를 띄운다.
 * 호출부는 한 줄로 쓸 수 있다:
 *   if (!(await confirmOffHoursAlert({ ... }))) return; // 사용자가 닫음 → 액션 취소
 *
 * @returns 액션을 진행해도 되면 true, 사용자가 닫아 취소했거나 다이얼로그를 열 수 없으면 false.
 */
export async function confirmOffHoursAlert(params: ShouldShowOffHoursAlertParams): Promise<boolean> {
  if (!shouldShowOffHoursAlert(params)) return true;
  if (isGuardDialogInFlight) return false;
  isGuardDialogInFlight = true;
  // 주문 제출(openOrder)은 변형 A(Cancel order/Proceed), 관리 액션은 변형 B(Got It) — ERD-240 §8.2
  const variant: OffHoursAlertVariant = params.action === 'openOrder' ? 'order' : 'notice';
  try {
    let mounted = false;
    const opened = dialog.open<void>(({ isOpen, unmount, promise }) => {
      mounted = true;
      return <OffHoursAlertDialog isOpen={isOpen} onExit={unmount} variant={variant} promise={promise} />;
    });
    const dropped = new Promise<'dropped'>((resolve) => {
      setTimeout(() => {
        if (!mounted) resolve('dropped');
      }, dialogOpenGraceMs);
    });
    const result = await Promise.race([opened.then(() => 'confirmed' as const), dropped]);
    return result === 'confirmed';
  } catch {
    // backdrop/ESC 로 닫음 (Got It 아님) → 액션 취소
    return false;
  } finally {
    isGuardDialogInFlight = false;
  }
}
